import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import type { LikerUser } from "../types/social";

export interface UseLikersResult {
  users: LikerUser[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
}

/**
 * Hook for fetching users who liked a post with cursor-based pagination
 *
 * Features:
 * - Only fetches when `enabled` is true (modal open)
 * - Supports infinite scroll via loadMore
 * - Deduplicates users by userId when appending pages
 * - Provides reset() to clear state when modal closes
 *
 * @param postId - The post to load likers for
 * @param enabled - Whether fetching is active (e.g., modal is open)
 * @returns Likers state and control functions
 */
export function useLikers(postId: string, enabled: boolean): UseLikersResult {
  const [users, setUsers] = useState<LikerUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Track the current postId to avoid stale responses
  const currentPostIdRef = useRef<string>(postId);

  /**
   * Fetches likers from the API
   * @param cursorValue - Optional cursor for pagination
   * @param append - Whether to append results or replace
   */
  const fetchLikers = useCallback(
    async (cursorValue: string | null, append: boolean) => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.social.getPostLikers(
          postId,
          cursorValue || undefined
        );

        // Guard against stale responses if postId changed
        if (currentPostIdRef.current !== postId) {
          return;
        }

        if (append) {
          // Append new users, deduplicating by userId
          setUsers((prev) => {
            const existingIds = new Set(prev.map((u) => u.userId));
            const newUsers = response.users.filter(
              (u) => !existingIds.has(u.userId)
            );
            return [...prev, ...newUsers];
          });
        } else {
          setUsers(response.users);
        }

        setCursor(response.nextCursor);
        setHasMore(response.hasMore);
      } catch (err) {
        // Guard against stale responses
        if (currentPostIdRef.current !== postId) {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load likers";
        setError(errorMessage);
      } finally {
        if (currentPostIdRef.current === postId) {
          setLoading(false);
        }
      }
    },
    [postId]
  );

  /**
   * Load more users using the current cursor
   * Guarded by hasMore and loading state
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loading) {
      return;
    }
    fetchLikers(cursor, true);
  }, [hasMore, loading, cursor, fetchLikers]);

  /**
   * Reset all state — call when modal closes
   */
  const reset = useCallback(() => {
    setUsers([]);
    setCursor(null);
    setHasMore(false);
    setError(null);
    setLoading(false);
  }, []);

  // Fetch likers when enabled becomes true, or reset when disabled
  useEffect(() => {
    currentPostIdRef.current = postId;

    if (!enabled) {
      return;
    }

    // Reset state and fetch fresh data when enabled
    setUsers([]);
    setCursor(null);
    setHasMore(false);
    setError(null);
    fetchLikers(null, false);
  }, [postId, enabled, fetchLikers]);

  return {
    users,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
  };
}
